import { useEffect, useState } from 'preact/hooks';
import { PlaceholderScreen } from './PlaceholderScreen';
import type { AuditionStage } from '../routes/route';
import { listAllRecordsByType, ApiError, type RecordRow } from '../lib/api';
import { openRecordEditor, useRecordsVersion } from '../lib/recordEditor';
import './Auditions.css';

/** Opens the generic create form for a new audition (RecordForm engine). */
function newAudition() {
  openRecordEditor({ mode: 'create', typeKey: 'actor_audition' });
}

export interface AuditionsProps {
  tenantId: string;
  stage: AuditionStage;
  onOpen: (id: string) => void;
  // R1b — the id of the row shown in the detail pane (two-pane wide mode),
  // so this list can mark it selected. Undefined on phone / bare list.
  selectedId?: string;
}

// B2: the Strip's kebab-case ids -> actor_audition's own snake_case
// lifecycle_state values (packages/verticals/actor/src/index.ts). 'all'
// deliberately has no entry -- GET /records omits lifecycle_state entirely
// for it (no filter), rather than this map inventing a value that isn't a
// real state.
const STAGE_TO_LIFECYCLE: Partial<Record<AuditionStage, string>> = {
  'self-submitted': 'self_submitted',
  audition: 'audition',
  callback: 'callback',
  'chemistry-read': 'chemistry_read',
  'screen-test': 'screen_test',
  pinned: 'pinned',
  booked: 'booked',
  archived: 'archived',
};

// THE one stage-label map — sentence case, shared by this list, the
// detail's stepper, and the project detail's audition rows, so a stage
// never wears three spellings.
export const STAGE_LABEL: Record<string, string> = {
  self_submitted: 'Self-submitted',
  audition: 'Audition',
  callback: 'Callback',
  chemistry_read: 'Chemistry read',
  screen_test: 'Screen test',
  pinned: 'Pinned',
  booked: 'Booked',
  archived: 'Archived',
};

// B21 — Partial, not total: `AuditionStage` grew a `submissions` member for
// actor_assist's own Strip (HouseholdAuditions.tsx), which this OLD pack's
// screen has no concept of at all (its own Strip below only ever offers
// AUDITION_STAGES, which does not include it). A hand-typed
// `#/auditions/submissions` on a July-pack tenant is the one way `stage`
// could still arrive here as `'submissions'` — the `copy` lookup below
// falls back to `all`'s own copy for it, the same "unrecognised sub-segment
// behaves like the broadest one" rule findId already applies everywhere
// else in this router.
const COPY: Partial<Record<AuditionStage, { line: string; soft?: string }>> = {
  all: { line: 'Nothing new to review.', soft: 'A captured breakdown lands here.' },
  'self-submitted': { line: 'Nothing self-submitted right now.' },
  audition: { line: 'No auditions waiting.' },
  callback: { line: 'No callbacks waiting.' },
  'chemistry-read': { line: 'No chemistry reads waiting.' },
  'screen-test': { line: 'No screen tests waiting.' },
  pinned: { line: 'Nothing pinned.' },
  booked: { line: 'Nothing booked yet.' },
  archived: { line: 'Nothing archived.', soft: 'Quietly parked ones land here, back in a tap.' },
};

type LoadState = 'loading' | 'ready' | 'error';

export function formatWhen(iso: unknown): string | null {
  if (typeof iso !== 'string' || iso.length === 0) return null;
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function fieldText(record: RecordRow, name: string): string | null {
  const value = record.fields[name];
  return typeof value === 'string' && value.length > 0 ? value : null;
}

// Real auditions, flat reverse-chron, filtered by the stage Strip (B1) —
// All is the default (no lifecycle_state filter at all). B2: replaces the
// B1 always-empty scaffold with the real generic Records read
// (lib/api.ts's listRecordsByType — no dedicated actor list route needed,
// see plugins/actorAuditions.ts's own header for which reads DID need one).
export function Auditions({ tenantId, stage, onOpen, selectedId }: AuditionsProps) {
  const [state, setState] = useState<LoadState>('loading');
  const [records, setRecords] = useState<RecordRow[]>([]);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  // R3 — reload the list after a create/edit/act anywhere.
  const recordsVersion = useRecordsVersion();

  useEffect(() => {
    let cancelled = false;

    async function load() {
      setState('loading');
      try {
        const rows = await listAllRecordsByType(tenantId, 'actor_audition', STAGE_TO_LIFECYCLE[stage]);
        if (cancelled) return;
        // records.ts's generic list is oldest-first (ORDER BY created_at
        // ASC) -- reversed here for the reverse-chron read this surface
        // wants, rather than the generic primitive itself picking a
        // direction one caller happens to prefer.
        setRecords([...rows].reverse());
        setState('ready');
      } catch (err) {
        if (cancelled) return;
        setErrorMessage(err instanceof ApiError ? err.message : 'Could not reach the server.');
        setState('error');
      }
    }

    void load();
    return () => {
      cancelled = true;
    };
  }, [tenantId, stage, recordsVersion]);

  const copy = COPY[stage] ?? COPY.all!;

  if (state === 'loading') {
    return <PlaceholderScreen eyebrow="Auditions" line="One moment…" />;
  }

  if (state === 'error') {
    return (
      <PlaceholderScreen eyebrow="Auditions" line="Couldn't load auditions." soft={errorMessage ?? undefined} />
    );
  }

  if (records.length === 0) {
    return (
      <div class="auditions">
        <PlaceholderScreen eyebrow="Auditions" line={copy.line} soft={copy.soft} />
        <div class="auditions__empty-cta">
          <button type="button" class="list-new-btn" onClick={newAudition} data-testid="new-audition">
            + New audition
          </button>
        </div>
      </div>
    );
  }

  return (
    <div class="auditions">
      <div class="auditions__head">
        <div class="auditions__eyebrow">Auditions</div>
        <button type="button" class="list-new-btn" onClick={newAudition} data-testid="new-audition">
          + New
        </button>
      </div>
      <div class="auditions__list">
        {records.map((record) => {
          const project = fieldText(record, 'project') ?? record.display_name;
          const role = fieldText(record, 'role');
          const due = formatWhen(record.fields['due_date']);
          const stageLabel = record.lifecycle_state ? (STAGE_LABEL[record.lifecycle_state] ?? record.lifecycle_state) : '';
          const active = record.id === selectedId;
          return (
            <button
              type="button"
              key={record.id}
              class={active ? 'audition-card audition-card--active' : 'audition-card'}
              aria-current={active ? 'true' : undefined}
              onClick={() => onOpen(record.id)}
            >
              <div class="audition-card__row">
                <span class="audition-card__name">
                  {project}
                  {role ? ` — ${role}` : ''}
                </span>
                <span class="audition-card__mini">{stageLabel}</span>
              </div>
              <div class="audition-card__sub">{due ? `Due ${due}` : 'No date set'}</div>
            </button>
          );
        })}
      </div>
    </div>
  );
}
